import { useEffect, useMemo, useState } from 'react';

import {
  type LocationSearchPractice,
  usePracticePostalCode,
  useSearchByLocation,
} from '@/react-query/useReferralInfo';
import { useDebounce } from '@/utils/hooks/useDebounce';

import {
  extractSpecialtiesFromResults,
  filterPracticesBySpecialty,
} from '../components/TreatingSpecialist/locationSearchUtils';

interface UseLocationSpecialistSearchParams {
  videaPracticeId: string;
  selectedSpecialtyId?: number;
  selectedPractice: LocationSearchPractice | null;
}

export const useLocationSpecialistSearch = ({
  videaPracticeId,
  selectedSpecialtyId,
  selectedPractice,
}: UseLocationSpecialistSearchParams) => {
  const [postalCode, setPostalCode] = useState('');
  const { debouncedValue: debouncedPostalCode } = useDebounce(postalCode, 500);
  const [radius, setRadius] = useState('50');

  // Fetch the practice's postal code
  const { data: practicePostalCodeData } =
    usePracticePostalCode(videaPracticeId);

  // Set default postal code when practice postal code data is available
  useEffect(() => {
    if (practicePostalCodeData?.postalCode && !postalCode) {
      setPostalCode(practicePostalCodeData.postalCode);
    }
  }, [practicePostalCodeData, postalCode]);

  // Fetch location-based search results
  const {
    data: locationSearchResults,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
  } = useSearchByLocation(
    {
      postalCode: debouncedPostalCode,
      radiusKm: parseInt(radius, 10),
      specialtyIds: undefined, // Don't filter by specialty initially - get all practices
    },
    {
      enabled: debouncedPostalCode.length >= 5,
      keepPreviousData: true,
    },
  );

  // Extract unique specialties from the search results
  const specialties = useMemo(() => {
    return extractSpecialtiesFromResults(locationSearchResults?.data || []);
  }, [locationSearchResults]);

  // Filter practices by selected specialty
  const practices = useMemo(() => {
    const allPractices = locationSearchResults?.data || [];
    return filterPracticesBySpecialty(allPractices, selectedSpecialtyId);
  }, [locationSearchResults?.data, selectedSpecialtyId]);

  // Get specialists for the selected practice, filtered by specialty if selected
  const specialists = useMemo(() => {
    if (!selectedPractice) return [];

    return selectedPractice.specialists.filter(
      (specialist) =>
        !selectedSpecialtyId || specialist.specialtyId === selectedSpecialtyId,
    );
  }, [selectedPractice, selectedSpecialtyId]);

  return {
    // Location search state
    postalCode,
    setPostalCode,
    debouncedPostalCode,
    radius,
    setRadius,

    // Search results
    locationSearchResults,
    specialties,
    practices,
    specialists,

    // Loading states
    isSearchLoading,
    isSearchFetching,
    isSpecialtiesLoading: isSearchLoading,
    isPracticesLoading: isSearchLoading || isSearchFetching,
  };
};
